<template>
    <base-dialog :value="dialog" :title="title" :width="520" :fullscreen="isMobile" @input="onInput" @close="hide">
        <div class="form-group">
            <span class="form-label">{{ $t('common.name') }}</span>
            <kimsa-text-field :placeholder="$t('business.client_name_placeholder')" :value="name" @change="name = $event"
                dense force-value />
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('common.email') }}</span>
            <kimsa-text-field placeholder="ejemplo@mail.com" :value="email" @change="email = $event"
                :disabled="fromApp" dense force-value />
            <span class="form-hint">{{ fromApp ? $t('business.client_from_app_hint') : $t('business.client_email_hint') }}</span>
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.plan_type') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="planTypes" :value="planId"
                    item-text="name" item-value="id" no-object dense solo clearable @change="onChangePlan" />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.monthly_amount') }}</span>
                <kimsa-text-field placeholder="80" :value="planAmount" @change="planAmount = $event"
                    input-mode="numeric" dense force-value />
            </div>
        </div>

        <div class="form-group mt-4">
            <button v-if="!showExceptions" type="button" class="exceptions-toggle" @click="showExceptions = true">
                <v-icon size="14">mdi-plus</v-icon>
                {{ $t('business.add_exception') }}
            </button>

            <template v-else>
                <span class="form-label">{{ $t('business.exceptions') }}</span>
                <span class="form-hint mb-2">{{ $t('business.exceptions_hint') }}</span>
                <access-rules-editor :rules="exceptions" :activities="activities"
                    :empty-text="$t('business.no_exceptions')" :add-label="$t('business.add_exception')"
                    @change="exceptions = $event" />
            </template>
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.method') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="methodOptions" :value="method"
                    item-text="name" item-value="value" no-object dense solo clearable @change="method = $event" />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.joined_at') }}</span>
                <kimsa-date-picker :value="joinedAt" @change="joinedAt = $event" solo flat dense color="secondary"
                    force-value />
            </div>
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.next_payment') }}</span>
                <kimsa-date-picker :value="nextPayment" @change="nextPayment = $event" solo flat dense
                    color="secondary" force-value />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.origin') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="originOptions" :value="origin"
                    item-text="name" item-value="value" no-object dense solo clearable @change="origin = $event" />
            </div>
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.area') }}</span>
                <kimsa-text-field :placeholder="$t('business.area_placeholder')" :value="area" @change="area = $event"
                    dense force-value />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.sport') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="sportOptions" :value="sport"
                    item-text="label" item-value="value" no-object dense solo clearable @change="sport = $event" />
            </div>
        </div>

        <div class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.gender') }}</span>
                <kimsa-combobox :placeholder="$t('common.select_placeholder')" :items="genderOptions" :value="gender"
                    item-text="name" item-value="value" no-object dense solo clearable @change="gender = $event" />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.age') }}</span>
                <kimsa-text-field placeholder="25" :value="age" @change="age = $event" input-mode="numeric" dense
                    force-value />
            </div>
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('business.job') }}</span>
            <kimsa-text-field :placeholder="$t('business.job_placeholder')" :value="job" @change="job = $event" dense
                force-value />
        </div>

        <kimsa-toggle class="mt-5" :value="promoActive" @change="promoActive = $event">
            <span class="promo-label">{{ $t('business.apply_promo') }}</span>
        </kimsa-toggle>

        <div v-if="promoActive" class="form-row mt-4">
            <div class="form-group">
                <span class="form-label">{{ $t('business.discount_percent') }}</span>
                <kimsa-text-field placeholder="20" :value="promoPercent" @change="promoPercent = $event"
                    input-mode="numeric" dense force-value />
            </div>
            <div class="form-group">
                <span class="form-label">{{ $t('business.promo_months') }}</span>
                <kimsa-text-field placeholder="3" :value="promoMonths" @change="promoMonths = $event"
                    input-mode="numeric" dense force-value />
            </div>
        </div>

        <span v-if="error" class="client-error">{{ error }}</span>

        <template v-slot:footer>
            <dialog-actions :confirm-label="confirmLabel" :disabled="!valid" :loading="saving" @cancel="hide"
                @confirm="save">
                <template v-slot:extra>
                    <button v-if="canDelete" type="button" class="client-delete" @click="$emit('delete', client)">
                        {{ $t('common.delete') }}
                    </button>
                </template>
            </dialog-actions>
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import AccessRulesEditor from "@/components/company/business/AccessRulesEditor";
import { mapState } from "vuex";
import {
    CLIENT_ORIGINS, GENDER_OPTIONS, PAYMENT_METHODS, emailTaken, normalizeRules, parseAmount, today, toDateInput,
} from "@/utils/Business";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
    name: "ClientDialog",
    components: {
        BaseDialog, DialogActions, KimsaTextField, KimsaCombobox, KimsaDatePicker, KimsaToggle, AccessRulesEditor,
    },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        planTypes: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            dialog: false,
            saving: false,
            client: null,
            exceptions: [],
            showExceptions: false,
            name: '',
            email: '',
            planId: null,
            planAmount: '',
            method: '',
            joinedAt: '',
            nextPayment: '',
            origin: '',
            area: '',
            sport: '',
            gender: '',
            age: '',
            job: '',
            promoActive: false,
            promoPercent: '',
            promoMonths: '',
            duplicated: false,
        }
    },
    computed: {
        ...mapState(['config']),
        isEdit(vm = this) {
            return !!(vm.client?.id || vm.client?.studentId)
        },
        canDelete(vm = this) {
            return !!vm.client?.id
        },
        fromApp(vm = this) {
            return !!vm.client?.studentId
        },
        title(vm = this) {
            return vm.isEdit ? vm.$t('business.client_file') : vm.$t('business.new_client')
        },
        confirmLabel(vm = this) {
            return vm.isEdit ? vm.$t('common.save') : vm.$t('common.create')
        },
        methodOptions(vm = this) {
            void vm.$i18n.locale
            return PAYMENT_METHODS.map(value => ({ value, name: vm.$t(`business.method_${value}`) }))
        },
        originOptions(vm = this) {
            void vm.$i18n.locale
            return CLIENT_ORIGINS.map(value => ({ value, name: vm.$t(`business.origin_${value}`) }))
        },
        genderOptions(vm = this) {
            void vm.$i18n.locale
            return GENDER_OPTIONS.map(({ value, key }) => ({ value, name: vm.$t(`business.${key}`) }))
        },
        sportOptions(vm = this) {
            void vm.$i18n.locale
            return (vm.config?.sports || []).map(sport => ({ value: sport, label: vm.sportLabel(sport) }))
        },
        invalidEmail(vm = this) {
            return !!vm.email && !EMAIL_REGEX.test(String(vm.email).trim())
        },
        error(vm = this) {
            if (vm.duplicated) return vm.$t('business.email_taken')
            if (vm.invalidEmail) return vm.$t('business.invalid_email')
            return ''
        },
        valid(vm = this) {
            return !!String(vm.name || '').trim() && !!vm.joinedAt && !vm.invalidEmail && !vm.duplicated
        },
    },
    methods: {
        show(client = null) {
            const vm = this
            vm.client = client
            vm.name = client?.name || ''
            vm.email = client?.email || ''
            vm.planId = client?.planId || null
            vm.planAmount = client?.planAmount || ''
            vm.method = client?.method || ''
            vm.joinedAt = toDateInput(client?.joinedAt) || today()
            vm.nextPayment = toDateInput(client?.nextPayment)
            vm.origin = client?.origin || ''
            vm.area = client?.area || ''
            vm.sport = client?.sport || ''
            vm.gender = client?.gender || ''
            vm.age = client?.age || ''
            vm.job = client?.job || ''
            vm.promoActive = !!client?.promo?.active
            vm.promoPercent = client?.promo?.percent || ''
            vm.promoMonths = client?.promo?.months || ''
            vm.exceptions = (client?.exceptions || []).map(rule => ({ ...rule, days: [...rule.days] }))
            vm.showExceptions = vm.exceptions.length > 0
            vm.duplicated = false
            vm.saving = false
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        onChangePlan(planId) {
            const vm = this
            vm.planId = planId
            const plan = vm.planTypes.find(item => item.id === planId)
            if (plan && !parseAmount(vm.planAmount)) vm.planAmount = plan.amount
        },
        async save() {
            const vm = this
            if (!vm.valid || vm.saving) return
            vm.saving = true
            try {
                vm.duplicated = await emailTaken(vm.companyId, vm.email, vm.client?.id || null)
                if (vm.duplicated) return
                vm.$emit('save', {
                    id: vm.client?.id || null,
                    studentId: vm.client?.studentId || null,
                    active: vm.client?.active !== false,
                    name: vm.name,
                    email: vm.email,
                    planId: vm.planId,
                    planAmount: vm.planAmount,
                    method: vm.method,
                    joinedAt: vm.joinedAt,
                    nextPayment: vm.nextPayment,
                    origin: vm.origin,
                    area: vm.area,
                    sport: vm.sport,
                    gender: vm.gender,
                    age: vm.age,
                    job: vm.job,
                    promo: vm.promoActive
                        ? { active: true, percent: parseAmount(vm.promoPercent), months: parseAmount(vm.promoMonths) }
                        : null,
                    exceptions: normalizeRules(vm.exceptions),
                })
                vm.hide()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.saving = false
            }
        },
    },
    watch: {
        email() {
            this.duplicated = false
        },
    },
}
</script>

<style scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.exceptions-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtSecondary-base);
    transition: color 0.15s;
}

.exceptions-toggle:hover {
    color: var(--v-txtPrimary-base);
}

.form-hint {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.promo-label {
    margin-left: 10px;
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.client-error {
    display: block;
    margin-top: 14px;
    font-size: 11px;
    color: var(--v-error-base);
}

.client-delete {
    padding: 0 20px;
    height: 52px;
    border-right: 1px solid var(--v-outline-base);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--v-error-base);
}

@media (max-width: 600px) {
    .form-row {
        grid-template-columns: 1fr;
    }
}
</style>
